/**
 * 调试面板 - 用于在 iPad Standalone Mode 下查看日志
 *
 * 使用方法：在任意页面添加 <DebugPanel />
 */
import { useState, useEffect } from 'react';
import { isIOSStandaloneMode } from '@/utils/platform';

interface LogEntry {
  time: string;
  message: string;
  type: 'log' | 'warn' | 'error';
}

export function DebugPanel() {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isStandalone] = useState(isIOSStandaloneMode());

  useEffect(() => {
    // 拦截 console.log
    const originalLog = console.log;
    const originalWarn = console.warn;
    const originalError = console.error;

    const addLog = (message: string, type: 'log' | 'warn' | 'error') => {
      const time = new Date().toLocaleTimeString();
      setLogs(prev => [...prev.slice(-50), { time, message, type }]);
    };

    console.log = (...args: any[]) => {
      originalLog(...args);
      addLog(args.map(a => String(a)).join(' '), 'log');
    };

    console.warn = (...args: any[]) => {
      originalWarn(...args);
      addLog(args.map(a => String(a)).join(' '), 'warn');
    };

    console.error = (...args: any[]) => {
      originalError(...args);
      addLog(args.map(a => String(a)).join(' '), 'error');
    };

    return () => {
      console.log = originalLog;
      console.warn = originalWarn;
      console.error = originalError;
    };
  }, []);

  if (!isStandalone) {
    return null; // 只在 Standalone Mode 下显示
  }

  return (
    <>
      {/* 悬浮按钮 */}
      <button
        onClick={() => setIsVisible(!isVisible)}
        style={{
          position: 'fixed',
          top: '10px',
          right: '10px',
          zIndex: 9999,
          width: '50px',
          height: '50px',
          borderRadius: '25px',
          backgroundColor: isVisible ? '#ff4d4f' : 'var(--theme-primary)',
          color: 'white',
          border: 'none',
          fontSize: '20px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          cursor: 'pointer',
        }}
      >
        {isVisible ? '✕' : '🐛'}
      </button>

      {/* 日志面板 */}
      {isVisible && (
        <div
          style={{
            position: 'fixed',
            top: '70px',
            left: '10px',
            right: '10px',
            bottom: '10px',
            zIndex: 9998,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            color: '#00ff00',
            padding: '10px',
            borderRadius: '8px',
            overflow: 'auto',
            fontFamily: 'monospace',
            fontSize: '12px',
            lineHeight: '1.5',
          }}
        >
          <div style={{ marginBottom: '10px', borderBottom: '1px solid #333', paddingBottom: '5px' }}>
            <strong style={{ color: 'var(--theme-primary)' }}>Debug Panel (Standalone Mode)</strong>
            <button
              onClick={() => setLogs([])}
              style={{
                float: 'right',
                padding: '2px 8px',
                fontSize: '10px',
                backgroundColor: '#333',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
              }}
            >
              清空
            </button>
          </div>

          <div style={{ marginBottom: '10px', padding: '5px', backgroundColor: '#1a1a1a', borderRadius: '4px' }}>
            <div>window.innerHeight: {window.innerHeight}px</div>
            <div>visualViewport.height: {window.visualViewport?.height}px</div>
            <div>差异: {window.innerHeight - (window.visualViewport?.height || 0)}px</div>
            <div>--app-vh: {getComputedStyle(document.documentElement).getPropertyValue('--app-vh')}</div>
          </div>

          {logs.map((log, i) => (
            <div
              key={i}
              style={{
                color: log.type === 'error' ? '#ff4d4f' : log.type === 'warn' ? '#faad14' : '#00ff00',
                marginBottom: '5px',
              }}
            >
              <span style={{ color: '#666' }}>[{log.time}]</span> {log.message}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
